"use client";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  createTrainer,
  updateTrainer,
  getTrainerById,
} from "../../../services/TrainerService";
import { toast } from '../common/Toast/toast';
import { useNavigate, useParams } from "react-router-dom";
import { getUsers } from "../../../services/UserSerivce.js";
import { useAuth } from "../../../context/AuthContext";
import NewCustomAsyncDropdown from "../../components/common/NewCustomAsyncSelectDropdown.jsx";
import debounce from "lodash/debounce";
import "./TrainerDashboard.css";
import "../common/lms-pages.css";

// Rendered on its own route, and inside the trainer list as a dialog.
function AddTrainer({ embedded = false, recordId, onDone, onCancel }) {
  const { id: routeId } = useParams();
  const id = recordId || routeId;
  const [trainerData, setTrainerData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // User dropdown states (infinite scroll + search)
  const [userOptions, setUserOptions] = useState([]);
  const [userPage, setUserPage] = useState(1);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [selectedUserOption, setSelectedUserOption] = useState(null);
  const isFetching = useRef(false);
  const hasLoadedInitial = useRef(false);

  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();

  // Permission check
  useEffect(() => {
    if (!hasPermission("list-trainer", "add", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to add or edit trainers.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // Fetch users from API with pagination and search
  const fetchUsers = useCallback(async (search = "", pageNum = 1) => {
    try {
      isFetching.current = true;
      // Adjust parameters according to your getUsers API (limit, page, search)
      const response = await getUsers(20, pageNum, search, "firstName");
      const users = Array.isArray(response.data) ? response.data : [];
      // The API's own total decides whether there is another page. Guessing
      // from "this page came back full" stops early the moment the server
      // sends a page of a different size than the one asked for.
      const total = Number(response.pagination?.total);
      const hasMore = Number.isFinite(total)
        ? pageNum * 20 < total
        : users.length === 20;
      return { users, hasMore };
    } catch (err) {
      console.error("Error fetching users:", err);
      return { users: [], hasMore: false };
    } finally {
      isFetching.current = false;
    }
  }, []);

  // Load users for dropdown
  const loadUsers = useCallback(
    async (search = "", pageNum = 1, append = false) => {
      setLoadingUsers(true);
      const { users, hasMore } = await fetchUsers(search, pageNum);

      const newOptions = users.map((user) => ({
        value: user.id,
        label: `${user.firstName} ${user.lastName} - (${user.email})`,
        user: user, // store full user object
      }));

      if (append) {
        setUserOptions((prev) => [...prev, ...newOptions]);
      } else {
        setUserOptions(newOptions);
      }

      setHasMoreUsers(hasMore);
      setLoadingUsers(false);
      return { users, hasMore };
    },
    [fetchUsers]
  );

  // Load more users for infinite scroll
  const handleLoadMore = useCallback(async () => {
    if (!hasMoreUsers || loadingUsers || isFetching.current) return;
    const nextPage = userPage + 1;
    setUserPage(nextPage);
    await loadUsers(userSearchTerm, nextPage, true);
  }, [hasMoreUsers, loadingUsers, userPage, userSearchTerm, loadUsers]);

  // Debounced search handler
  const handleUserSearch = useMemo(
    () =>
      debounce(async (inputValue) => {
        setUserSearchTerm(inputValue);
        setUserPage(1);
        await loadUsers(inputValue, 1, false);
      }, 700),
    [loadUsers]
  );

  // Handle dropdown open - load initial users only once
  const handleDropdownOpen = useCallback(async () => {
    if (!hasLoadedInitial.current && userOptions.length === 0 && !loadingUsers) {
      await loadUsers("", 1, false);
      hasLoadedInitial.current = true;
    }
  }, [loadUsers, userOptions.length, loadingUsers]);

  /* Handle user selection. Formik's own setFieldValue is passed in from the
     render function. It used to be read from a piece of state that nothing
     ever filled, so picking a user set the dropdown but never trainerId --
     and submitting said "User is required" with a user plainly selected. */
  const handleUserSelection = (selectedOptions, setFieldValue) => {
    const selected = selectedOptions && selectedOptions.length > 0 ? selectedOptions[0] : null;
    setSelectedUserOption(selected);
    setFieldValue("trainerId", selected ? selected.value : "");
  };

  // Fetch trainer details if editing
  const fetchTrainerDetails = useCallback(async (trainerId) => {
    try {
      setLoading(true);
      const response = await getTrainerById(trainerId);
      if (response.data) {
        setTrainerData(response.data);
        setIsEditing(true);
        // Pre-select the user in dropdown for editing. trainerId itself
        // reaches the form through initialValues (enableReinitialize).
        // The record's field is trainerEmail; the lower-case spelling this
        // checked alone never matched, so editing opened with no user picked
        // and saving stopped at "Please select a user."
        const email = response.data.trainerEmail || response.data.traineremail;
        if (response.data.trainerId && response.data.trainerName && email) {
          const preSelectedOption = {
            value: response.data.trainerId,
            label: `${response.data.trainerName} - (${email})`,
            user: {
              id: response.data.trainerId,
              firstName: response.data.trainerName.split(" ")[0] || "",
              lastName: response.data.trainerName.split(" ").slice(1).join(" "),
              email,
            },
          };
          setSelectedUserOption(preSelectedOption);
        }
      } else {
        setTrainerData(null);
        setIsEditing(false);
        toast.error("Trainer not found!");
      }
    } catch (error) {
      console.error("Error fetching trainer details:", error);
      toast.error("Failed to load trainer details");
      setTrainerData(null);
      setIsEditing(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch trainer details if editing
  useEffect(() => {
    if (id) {
      fetchTrainerDetails(id);
    } else {
      setTrainerData(null);
      setIsEditing(false);
    }
  }, [id, fetchTrainerDetails]);

  // Validation schema (backend handles duplicate email)
  const validationSchema = Yup.object().shape({
    trainerId: Yup.string().required("User is required"),
    specialization: Yup.string()
      .required("Specialization is required")
      .min(3, "Specialization must be at least 3 characters")
      .max(100, "Specialization cannot exceed 100 characters"),
    experience: Yup.string().required("Experience is required"),
  });

  const initialValues = trainerData
    ? {
        trainerId: trainerData.trainerId || "",
        specialization: trainerData.specialization || "",
        experience: trainerData.experience || "",
      }
    : {
        trainerId: "",
        specialization: "",
        experience: "",
      };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (!selectedUserOption) {
        toast.error("Please select a user.");
        setSubmitting(false);
        return;
      }

      const selectedUser = selectedUserOption.user;
      const trainerPayload = {
        trainerId: values.trainerId,
        trainerName: `${selectedUser.firstName} ${selectedUser.lastName}`,
        trainerEmail: selectedUser.email,
        specialization: values.specialization,
        experience: values.experience,
      };

      if (isEditing) {
        await updateTrainer(id, trainerPayload);
        toast.success("Trainer updated successfully!");
      } else {
        await createTrainer(trainerPayload);
        toast.success("Trainer added successfully!");
      }

      resetForm();
      setTrainerData(null);
      setIsEditing(false);
      setSelectedUserOption(null);

      if (embedded) {
        onDone?.();
        return;
      }
      setTimeout(() => {
        navigate("/list-trainer");
      }, 1500);
    } catch (error) {
      console.error("Error processing trainer:", error);
      if (error.response && error.response.status === 409) {
        toast.error("A trainer with this email already exists!");
      } else if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Error processing trainer!");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="att-picklist-foot">
        <span className="pg-spinner att-spinner" aria-hidden="true" />
        <span>Loading trainer...</span>
      </div>
    );
  }

  const leave = () => {
    if (embedded) onCancel?.();
    else navigate("/list-trainer");
  };

  return (
    <div className={embedded ? "att-embed att-embed--picker" : "pg"}>
      {!embedded && (
        <header className="pg-head">
          <div className="pg-head-main">
            <h4 className="pg-title">
              {isEditing ? "Edit trainer" : "Add trainer"}
            </h4>
          </div>
          <div className="pg-head-actions">
            <button type="button" className="pg-btn" onClick={leave}>
              Back to trainers
            </button>
          </div>
        </header>
      )}

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue, handleSubmit: submit }) => {
          const err = (f) => (touched[f] ? errors[f] : "");
          const ready =
            selectedUserOption &&
            String(values.specialization || "").trim() &&
            String(values.experience || "").trim() &&
            !errors.specialization &&
            !errors.experience;

          return (
            <form className="att-form" noValidate onSubmit={submit}>
              <div className="att-stage">
                <div className="att-stage-inner">
                  {/* One step: who, what they teach, and for how long. */}
                  <section className="att-step">
                    <div className="pg-field">
                      <label className="pg-label">User</label>
                      <NewCustomAsyncDropdown
                        options={userOptions}
                        value={selectedUserOption ? [selectedUserOption] : []}
                        onChange={(opts) => handleUserSelection(opts, setFieldValue)}
                        placeholder="Search for a user..."
                        isMulti={false}
                        isSearchable={true}
                        searchInControl={true}
                        isLoading={loadingUsers}
                        onInputChange={handleUserSearch}
                        onMenuScrollToBottom={handleLoadMore}
                        onMenuOpen={handleDropdownOpen}
                        loadingMessage="Loading users..."
                        noOptionsMessage="No users available"
                        noSearchResultsMessage="No users found"
                      />
                      {err("trainerId") && (
                        <p className="att-error">{err("trainerId")}</p>
                      )}
                    </div>

                    <div className="mcq-options">
                      <div className="pg-field">
                        <label className="pg-label" htmlFor="at-spec">
                          Specialization
                        </label>
                        <input
                          id="at-spec"
                          type="text"
                          className="pg-input"
                          placeholder="e.g. Java, Spring Boot"
                          value={values.specialization || ""}
                          onChange={(e) =>
                            setFieldValue("specialization", e.target.value)
                          }
                        />
                        {err("specialization") && (
                          <p className="att-error">{err("specialization")}</p>
                        )}
                      </div>

                      <div className="pg-field">
                        <label className="pg-label" htmlFor="at-exp">
                          Experience (years)
                        </label>
                        <input
                          id="at-exp"
                          type="text"
                          inputMode="numeric"
                          className="pg-input"
                          placeholder="0"
                          value={values.experience ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (/^[0-9]*$/.test(v)) setFieldValue("experience", v);
                          }}
                        />
                        {err("experience") && (
                          <p className="att-error">{err("experience")}</p>
                        )}
                      </div>
                    </div>
                  </section>
                </div>
              </div>

              <div className="att-actions">
                <button type="button" className="pg-btn" onClick={leave}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="pg-btn pg-btn--main"
                  disabled={isSubmitting || !ready}
                >
                  {isSubmitting ? (
                    <>
                      <span className="pg-spinner att-spinner" aria-hidden="true" />
                      {isEditing ? "Updating..." : "Creating..."}
                    </>
                  ) : isEditing ? (
                    "Update trainer"
                  ) : (
                    "Create trainer"
                  )}
                </button>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
}

export default AddTrainer;
